import { DocumentSnapshot } from 'firebase/firestore';
import { PersonService } from './personService';
import TimelineService, { ExtendedLifeEvent } from './timelineService';
import { TreeRelationshipService } from './treeRelationshipService';
import {
  TimelineEvent,
  TimelineFilter,
  TimelineStatistics,
  GenerationGroup,
  TimelineAggregationOptions,
} from '../types/timeline';
import { Person, LifeEventType } from '../types/person';
import { UserRole } from '../types/auth';

/**
 * Timeline Aggregation Service
 * Aggregates events from all people in the family tree and provides filtering/grouping capabilities
 *
 * Part of Phase 3.3: Interactive Timeline Page
 */
export class TimelineAggregationService {
  /**
   * Aggregate all events from all visible people into a master timeline
   *
   * @param userRole - Current user's role for permission checking
   * @param options - Optional configuration for aggregation
   * @returns Array of timeline events with enriched person information
   */
  static async aggregateAllEvents(
    userRole: UserRole,
    options: TimelineAggregationOptions = {}
  ): Promise<TimelineEvent[]> {
    try {
      // Get all persons visible to the user (fetch in batches)
      const allPersons: Person[] = [];
      let hasMore = true;
      let lastDoc: DocumentSnapshot | undefined;

      while (hasMore) {
        const result = await PersonService.getPersons(100, lastDoc);
        allPersons.push(...result.persons);
        lastDoc = result.lastDoc || undefined;
        hasMore = result.hasMore;
      }

      // Filter based on permissions
      const visiblePersons = this.filterPersonsByPermissions(allPersons, userRole, options);

      // Get all events for all visible persons
      const allEvents: TimelineEvent[] = [];

      for (const person of visiblePersons) {
        try {
          const personEvents = await TimelineService.getPersonTimeline(person.id);

          // Enrich each event with person information
          const enrichedEvents = personEvents.map((event) =>
            this.enrichEvent(event, person)
          );

          allEvents.push(...enrichedEvents);
        } catch (error) {
          console.warn(`Failed to load events for person ${person.id}:`, error);
          // Continue with other persons
        }
      }

      // Calculate generations for all persons
      try {
        const generationMap = await this.calculateGenerations();

        // Enrich events with generation data
        allEvents.forEach(event => {
          const generation = generationMap.get(event.personId);
          if (generation !== undefined) {
            event.generation = generation;
          }
        });
      } catch (error) {
        console.warn('Failed to calculate generations:', error);
        // Continue without generation data
      }

      // Sort by date (most recent first by default)
      const sortDirection = options.sortDirection || 'desc';
      allEvents.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return sortDirection === 'desc' ? 1 : -1;
        if (!b.date) return sortDirection === 'desc' ? -1 : 1;

        return sortDirection === 'desc'
          ? b.date.localeCompare(a.date)
          : a.date.localeCompare(b.date);
      });

      // Apply max events limit if specified
      if (options.maxEvents && options.maxEvents > 0) {
        return allEvents.slice(0, options.maxEvents);
      }

      return allEvents;
    } catch (error) {
      console.error('Failed to aggregate timeline events:', error);
      throw new Error('Failed to load timeline events');
    }
  }

  /**
   * Filter persons based on user permissions
   * Note: PersonService already filters out deleted persons
   */
  private static filterPersonsByPermissions(
    persons: Person[],
    userRole: UserRole,
    options: TimelineAggregationOptions
  ): Person[] {
    // For now, all fetched persons are visible to the user
    // PersonService already handles deleted filtering
    // Future: Add privacy/permission logic here if needed
    return persons;
  }

  /**
   * Enrich a life event with person information for timeline display
   */
  private static enrichEvent(event: ExtendedLifeEvent, person: Person): TimelineEvent {
    return {
      id: event.id,
      personId: person.id,
      personName: `${person.firstName} ${person.lastName}`,
      personGender: person.gender as 'male' | 'female' | 'other' | 'unknown',
      type: event.type,
      date: event.date,
      location: event.location,
      description: event.description,
      sources: event.sources,
      photoIds: event.photoIds,
      relationshipIds: event.relationshipIds,
      createdBy: event.createdBy,
      createdAt: event.createdAt instanceof Date ? event.createdAt : new Date(),
      updatedAt: event.updatedAt,
      // Computed fields - generation and familyBranch will be populated later if needed
      generation: undefined,
      familyBranch: undefined,
      decade: event.date ? this.getDecade(event.date) : undefined,
      century: event.date ? this.getCentury(event.date) : undefined,
    };
  }

  /**
   * Filter timeline events based on filter criteria
   *
   * @param events - Array of timeline events
   * @param filter - Filter criteria
   * @returns Filtered array of timeline events
   */
  static filterEvents(events: TimelineEvent[], filter: TimelineFilter): TimelineEvent[] {
    let filtered = [...events];

    // Filter by person IDs
    if (filter.personIds && filter.personIds.length > 0) {
      filtered = filtered.filter(event => filter.personIds!.includes(event.personId));
    }

    // Filter by event types
    if (filter.eventTypes && filter.eventTypes.length > 0) {
      filtered = filtered.filter(event => filter.eventTypes!.includes(event.type));
    }

    // Filter by date range
    if (filter.startDate) {
      filtered = filtered.filter(event => !event.date || event.date >= filter.startDate!);
    }
    if (filter.endDate) {
      filtered = filtered.filter(event => !event.date || event.date <= filter.endDate!);
    }

    // Filter by decades
    if (filter.decades && filter.decades.length > 0) {
      filtered = filtered.filter(event => event.decade && filter.decades!.includes(event.decade));
    }

    // Filter by centuries
    if (filter.centuries && filter.centuries.length > 0) {
      filtered = filtered.filter(event => event.century && filter.centuries!.includes(event.century));
    }

    // Filter by generations
    // Note: Events without generation data (undefined) are included to show all events
    if (filter.generations && filter.generations.length > 0) {
      filtered = filtered.filter(event =>
        event.generation === undefined || filter.generations!.includes(event.generation)
      );
    }

    // Filter by family branches
    if (filter.familyBranches && filter.familyBranches.length > 0) {
      filtered = filtered.filter(event =>
        event.familyBranch && filter.familyBranches!.includes(event.familyBranch)
      );
    }

    // Text search in description, location, etc.
    if (filter.searchQuery && filter.searchQuery.trim()) {
      const query = filter.searchQuery.toLowerCase();
      filtered = filtered.filter(event => {
        return (
          event.description.toLowerCase().includes(query) ||
          event.personName.toLowerCase().includes(query) ||
          event.location?.city?.toLowerCase().includes(query) ||
          event.location?.state?.toLowerCase().includes(query) ||
          event.location?.country?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }

  /**
   * Calculate generations for all persons using TreeRelationshipService
   *
   * @returns Map of personId to generation number
   */
  static async calculateGenerations(): Promise<Map<string, number>> {
    try {
      const generationsMap = new Map<string, number>();

      // Find the oldest ancestor to use as root
      const rootAncestor = await PersonService.findOldestAncestor();
      if (!rootAncestor) {
        return generationsMap;
      }

      // Build descendant tree from root using TreeRelationshipService
      const treeService = TreeRelationshipService.getInstance();
      const tree = await treeService.buildEnhancedTree({
        rootPersonId: rootAncestor.id,
        viewMode: 'descendant',
        maxDepth: 50,
      });

      // Traverse tree and assign generations
      const assignGenerations = (node: any, generation: number) => {
        generationsMap.set(node.person.id, generation);

        // Recursively assign to children
        if (node.children && node.children.length > 0) {
          node.children.forEach((child: any) => {
            assignGenerations(child, generation + 1);
          });
        }
      };

      // Root is generation 0
      assignGenerations(tree.root, 0);

      return generationsMap;
    } catch (error) {
      console.error('Failed to calculate generations:', error);
      return new Map<string, number>();
    }
  }

  /**
   * Group timeline events by generation
   *
   * @param events - Array of timeline events
   * @returns Array of generation groups
   */
  static groupByGeneration(events: TimelineEvent[]): GenerationGroup[] {
    // Group events by generation
    const groups = new Map<number, TimelineEvent[]>();

    events.forEach(event => {
      if (event.generation !== undefined) {
        const existing = groups.get(event.generation) || [];
        existing.push(event);
        groups.set(event.generation, existing);
      } else {
        // Put events without generation in group -1
        const existing = groups.get(-1) || [];
        existing.push(event);
        groups.set(-1, existing);
      }
    });

    // Convert to GenerationGroup array and sort within each generation
    const generationGroups: GenerationGroup[] = [];

    // Sort generation numbers
    const sortedGenerations = Array.from(groups.keys()).sort((a, b) => a - b);

    sortedGenerations.forEach(generation => {
      const groupEvents = groups.get(generation)!;

      // Sort events within generation by date
      groupEvents.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return a.date.localeCompare(b.date);
      });

      // Get unique person IDs in this generation
      const peopleSet = new Set(groupEvents.map(e => e.personId));

      generationGroups.push({
        generation,
        label: generation === -1 ? 'Unknown' : `Generation ${generation + 1}`,
        events: groupEvents,
        people: peopleSet,
      });
    });

    return generationGroups;
  }

  /**
   * Calculate statistics for the timeline
   *
   * @param events - Array of timeline events
   * @returns Timeline statistics
   */
  static getTimelineStatistics(events: TimelineEvent[]): TimelineStatistics {
    const stats: TimelineStatistics = {
      totalEvents: events.length,
      dateRange: {},
      peopleIncluded: 0,
      eventsByGeneration: {},
      eventsByType: {} as Record<LifeEventType, number>,
      eventsByDecade: {},
      eventsByCentury: {},
    };

    // Count unique people
    const uniquePeople = new Set(events.map(e => e.personId));
    stats.peopleIncluded = uniquePeople.size;

    // Calculate date range
    const datedEvents = events.filter(e => e.date);
    if (datedEvents.length > 0) {
      const dates = datedEvents.map(e => e.date!).sort();
      stats.dateRange.earliest = dates[0];
      stats.dateRange.latest = dates[dates.length - 1];

      // Calculate span in years
      const earliestYear = parseInt(dates[0].substring(0, 4));
      const latestYear = parseInt(dates[dates.length - 1].substring(0, 4));
      stats.dateRange.spanYears = latestYear - earliestYear;
    }

    // Count by generation
    events.forEach(event => {
      if (event.generation !== undefined) {
        stats.eventsByGeneration[event.generation] =
          (stats.eventsByGeneration[event.generation] || 0) + 1;
      }
    });

    // Count by event type
    events.forEach(event => {
      stats.eventsByType[event.type] = (stats.eventsByType[event.type] || 0) + 1;
    });

    // Count by decade
    events.forEach(event => {
      if (event.decade) {
        stats.eventsByDecade[event.decade] = (stats.eventsByDecade[event.decade] || 0) + 1;
      }
    });

    // Count by century
    events.forEach(event => {
      if (event.century) {
        stats.eventsByCentury[event.century] = (stats.eventsByCentury[event.century] || 0) + 1;
      }
    });

    return stats;
  }

  /**
   * Get decade from ISO date string (e.g., "1985-01-01" -> "1980s")
   */
  private static getDecade(dateString: string): string {
    const year = parseInt(dateString.substring(0, 4));
    const decade = Math.floor(year / 10) * 10;
    return `${decade}s`;
  }

  /**
   * Get century from ISO date string (e.g., "1985-01-01" -> "20th")
   */
  private static getCentury(dateString: string): string {
    const year = parseInt(dateString.substring(0, 4));
    const century = Math.ceil(year / 100);

    // Format with ordinal suffix
    if (century === 21) return '21st';
    if (century === 20) return '20th';
    if (century === 19) return '19th';
    if (century === 18) return '18th';

    return `${century}th`;
  }
}

export default TimelineAggregationService;
